import { useCallback, useEffect, useRef, useState } from "react";

import type { LucideIcon } from "lucide-react";

import {

  Bell,

  Building2,

  Check,

  CheckCheck,

  LoaderCircle,

  RefreshCw,

  X,

  CheckCircle2,

  XCircle,

  FileText,

} from "lucide-react";

import { Link } from "react-router-dom";

import { Button } from "@/shared/components/ui/button";

import notificationsApi, {

  type NotificationItem,

} from "@/modules/hrPortal/api/notifications.api";

// NOTE: change this path to the actual file where getTeamMembers is defined.

import {

  getTeamMembers,

  getInterviewers,

  getReactivationTransferRequests,

  decideReactivationTransferRequest,

  getActiveTeamMemberTransferRequests,

  decideActiveTeamMemberTransferRequest,

  type ReactivationTransferRequest,

  type ActiveTeamMemberTransferRequest,

} from "@/modules/team/api/team.api";

import {

  getJobApprovalRequestById,

  getMyJobApprovalRequestById,

  approveJobRequest,

  rejectJobRequest,

} from "@/modules/jobs/api/job.api";

interface DashboardHeaderProps {

  user?: {

    id?: string | null;

    name?: string | null;

    fullName?: string | null;

    email?: string | null;

    role?: string | null;

  } | null;

  pageTitle?: string;

  pageIcon?: LucideIcon;

}

interface ApprovalRequest {

  id: string;

  requestType?: string;

  status?: string;

  requestedStatus?: string;

  submittedData?: Record<string, unknown> | null;

  comments?: string | null;

  requester?: {

    id?: string;

    name?: string;

    email?: string;

    role?: string;

  } | null;

  reviewer?: {

    id?: string;

    name?: string;

    email?: string;

  } | null;

}

function formatNotificationTime(dateString: string): string {

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-IN", {

    dateStyle: "medium",

    timeStyle: "short",

  }).format(date);

}

function formatLabel(value: string): string {

  return value

    .replace(/([a-z])([A-Z])/g, "$1 $2")

    .replace(/[\_-]/g, " ")

    .replace(/\b\w/g, (char) => char.toUpperCase());

}

const ISO_DATE_TIME_REGEX =

  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;

// Converts "2026-10-15T00:00:00.000Z" -> "15 Oct 2026" (date only)

function formatDateOnly(value: string): string | null {

  if (!ISO_DATE_TIME_REGEX.test(value.trim())) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("en-IN", {

    dateStyle: "medium",

    timeZone: "UTC",

  }).format(date);

}

// Team members are fetched once and cached as { id: name }.

let teamMemberNamesPromise: Promise<Record<string, string>> | null = null;

// Walks any response shape and collects { id/userId -> name } from every object.

function collectNames(

  node: unknown,

  map: Record<string, string>,

  depth = 0,

): void {

  if (!node || typeof node !== "object" || depth > 6) return;

  if (Array.isArray(node)) {

    node.forEach((item) => collectNames(item, map, depth + 1));

    return;

  }

  const record = node as Record<string, unknown>;

  const name = String(

    record.name ?? record.fullName ?? record.email ?? "",

  ).trim();

  if (name) {

    [record.id, record.userId, record.teamMemberId].forEach((idValue) => {

      if (idValue) map[String(idValue)] = name;

    });

  }

  Object.values(record).forEach((value) =>

    collectNames(value, map, depth + 1),

  );

}

async function loadTeamMemberNames(): Promise<Record<string, string>> {

  const map: Record<string, string> = {};

  const [teamResult, interviewersResult] = await Promise.allSettled([

    getTeamMembers(),

    getInterviewers(),

  ]);

  if (teamResult.status === "fulfilled") {

    collectNames(teamResult.value, map);

  } else {

    console.error("getTeamMembers failed:", teamResult.reason);

  }

  if (interviewersResult.status === "fulfilled") {

    collectNames(interviewersResult.value, map);

  } else {

    console.error("getInterviewers failed:", interviewersResult.reason);

  }

  if (Object.keys(map).length === 0) {

    console.warn("No team member names could be read from the API response.");

  }

  return map;

}

async function fetchUserNameById(id: string): Promise<string | null> {

  if (!teamMemberNamesPromise) {

    teamMemberNamesPromise = loadTeamMemberNames().catch((err) => {

      teamMemberNamesPromise = null; // allow retry on next open

      throw err;

    });

  }

  const map = await teamMemberNamesPromise;

  if (!map[id]) {

    console.warn(

      `User id ${id} not found in team API responses. Loaded ids:`,

      Object.keys(map),

    );

  }

  return map[id] ?? null;

}

function formatValue(value: unknown): string {

  if (value === null || value === undefined || value === "") {

    return "—";

  }

  if (typeof value === "boolean") {

    return value ? "Yes" : "No";

  }

  if (typeof value === "string") {

    const dateOnly = formatDateOnly(value);

    if (dateOnly) return dateOnly;

  }

  if (Array.isArray(value)) {

    return value

      .map((item) => {

        if (item && typeof item === "object") {

          const record = item as Record<string, unknown>;

          return String(

            record.name ??

            record.title ??

            record.label ??

            record.value ??

            JSON.stringify(item),

          );

        }

        return String(item);

      })

      .join("\n");

  }

  if (typeof value === "object") {

    return Object.entries(value as Record<string, unknown>)

      .map(([key, item]) => `${formatLabel(key)}: ${formatValue(item)}`)

      .join("\n");

  }

  return String(value);

}

function getSubmittedValue(

  data: Record<string, unknown>,

  ...keys: string[]

): unknown {

  const normalize = (value: string) =>

    value.replace(/[\_\s-]/g, "").toLowerCase();

  const key = Object.keys(data).find((item) =>

    keys.some((candidate) => normalize(candidate) === normalize(item)),

  );

  return key ? data[key] : undefined;

}

function getDisplayList(value: unknown): string[] {

  if (value === null || value === undefined || value === "") {

    return [];

  }

  let normalizedValue = value;

  if (typeof normalizedValue === "string") {

    const trimmed = normalizedValue.trim();

    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {

      try {

        normalizedValue = JSON.parse(trimmed);

      } catch {

        // Keep plain text when it is not valid JSON.

      }

    }

  }

  if (Array.isArray(normalizedValue)) {

    return normalizedValue

      .map((item) => {

        if (typeof item === "string" || typeof item === "number") {

          return String(item).trim();

        }

        if (item && typeof item === "object") {

          const record = item as Record<string, unknown>;

          return String(

            record.name ??

            record.title ??

            record.label ??

            record.value ??

            "",

          ).trim();

        }

        return "";

      })

      .filter(Boolean);

  }

  if (typeof normalizedValue === "string") {

    return normalizedValue

      .split(/\r?\n/)

      .map((item) => item.trim())

      .filter(Boolean);

  }

  return [formatValue(normalizedValue)];

}

export default function DashboardHeader({

  user,

  pageTitle = "Dashboard",

  pageIcon: PageIcon,

}: DashboardHeaderProps) {

  const displayName =

    user?.name?.trim() || user?.fullName?.trim() || "User";

  const displayRole = user?.role?.trim() || "HR";

  const avatarLetter = displayName.charAt(0).toUpperCase();

  const normalizedRole = user?.role?.trim().toUpperCase();

  const canReviewApprovals =

    normalizedRole === "ADMIN" || normalizedRole === "HR";

  const isApprovalResultNotification = (

    notification: NotificationItem,

  ) =>

    [

      "JOB_APPROVED",

      "JOB_REJECTED",

      "JOB_CHANGES_REQUESTED",

    ].includes(notification.eventType?.toUpperCase() ?? "");

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const [unreadCount, setUnreadCount] = useState(0);

  const [isOpen, setIsOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [markingId, setMarkingId] = useState<string | null>(null);

  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const [selectedRequest, setSelectedRequest] =

    useState<ApprovalRequest | null>(null);

  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  const [isReviewing, setIsReviewing] = useState(false);

  const [reviewError, setReviewError] = useState<string | null>(null);

  const [rejectReason, setRejectReason] = useState("");

  const [showRejectForm, setShowRejectForm] = useState(false);

  const [assignedUserNames, setAssignedUserNames] = useState<

    Record<string, string>

  >({});

  const [incomingTransferRequests, setIncomingTransferRequests] = useState<ReactivationTransferRequest[]>([]);

  const [myTransferRequests, setMyTransferRequests] = useState<ReactivationTransferRequest[]>([]);

  const [transferRequestsLoading, setTransferRequestsLoading] = useState(false);

  const [processingTransferRequestId, setProcessingTransferRequestId] = useState<string | null>(null);
  const [incomingActiveTransferRequests, setIncomingActiveTransferRequests] = useState<ActiveTeamMemberTransferRequest[]>([]);
  const [myActiveTransferRequests, setMyActiveTransferRequests] = useState<ActiveTeamMemberTransferRequest[]>([]);
  const [activeTransferRequestsLoading, setActiveTransferRequestsLoading] = useState(false);
  const [processingActiveTransferRequestId, setProcessingActiveTransferRequestId] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {

    const data = selectedRequest?.submittedData;

    if (!data) return;

    // If the server already sends names, no lookup is needed.

    const providedNames = getDisplayList(

      getSubmittedValue(

        data,

        "assignedUsers",

        "assignedToUsers",

        "assignedToNames",

      ),

    );

    if (providedNames.length > 0) return;

    const ids = getDisplayList(

      getSubmittedValue(data, "assignedToIds", "assignedToId"),

    );

    let cancelled = false;

    ids.forEach((id) => {

      void fetchUserNameById(id)

        .then((name) => {

          if (cancelled) return;

          setAssignedUserNames((current) => ({

            ...current,

            [id]: name || "Name unavailable",

          }));

        })

        .catch(() => {

          if (cancelled) return;

          setAssignedUserNames((current) => ({

            ...current,

            [id]: "Name unavailable",

          }));

        });

    });

    return () => {

      cancelled = true;

    };

  }, [selectedRequest]);

  const loadTransferRequests = useCallback(async () => {

    try {

      setTransferRequestsLoading(true);

      const result = await getReactivationTransferRequests();

      setIncomingTransferRequests(Array.isArray(result?.incomingRequests) ? result.incomingRequests : []);

      setMyTransferRequests(Array.isArray(result?.myRequests) ? result.myRequests : []);

    } catch (err) {

      console.error("Failed to load reactivation transfer requests:", err);

    } finally {

      setTransferRequestsLoading(false);

    }

  }, []);



  const handleTransferDecision = async (

    request: ReactivationTransferRequest,

    decision: "APPROVE" | "REJECT",

  ) => {

    if (processingTransferRequestId) return;

    setProcessingTransferRequestId(request.id);

    try {

      const result = await decideReactivationTransferRequest(request.id, decision);

      await loadTransferRequests();

      await loadNotifications();

      window.alert(result?.message || (decision === "APPROVE"

        ? "The Team Member has been transferred and reactivated."

        : "The transfer and reactivation request was rejected."));

    } catch (err: any) {

      console.error("Failed to decide transfer request:", err);

      window.alert(err?.response?.data?.message || err?.message || "Unable to process the transfer request.");

      await loadTransferRequests();

    } finally {

      setProcessingTransferRequestId(null);

    }

  };



  const loadNotifications = useCallback(async () => {

    setIsLoading(true);

    setError(null);

    try {

      const [list, count] = await Promise.all([

        notificationsApi.getNotifications(1, 10),

        notificationsApi.getUnreadCount(),

      ]);

      setNotifications(list.notifications ?? []);

      setUnreadCount(count);

    } catch (err) {

      console.error("Failed to load notifications:", err);

      setError("Unable to load notifications. Please try again.");

    } finally {

      setIsLoading(false);

    }

  }, []);

  useEffect(() => {

    void loadNotifications();

  }, [loadNotifications]);

  useEffect(() => {

    function handleOutsideClick(event: MouseEvent) {

      if (

        dropdownRef.current &&

        !dropdownRef.current.contains(event.target as Node)

      ) {

        setIsOpen(false);

      }

    }

    function handleEscape(event: KeyboardEvent) {

      if (event.key === "Escape") {

        setIsOpen(false);

        setSelectedRequest(null);

        setShowRejectForm(false);

      }

    }

    document.addEventListener("mousedown", handleOutsideClick);

    document.addEventListener("keydown", handleEscape);

    return () => {

      document.removeEventListener("mousedown", handleOutsideClick);

      document.removeEventListener("keydown", handleEscape);

    };

  }, []);

  const loadActiveTransferRequests = useCallback(async () => {
    try {
      setActiveTransferRequestsLoading(true);
      const result = await getActiveTeamMemberTransferRequests();
      setIncomingActiveTransferRequests(Array.isArray(result?.incomingRequests) ? result.incomingRequests : []);
      setMyActiveTransferRequests(Array.isArray(result?.myRequests) ? result.myRequests : []);
    } catch (err) {
      console.error("Failed to load active team member transfer requests:", err);
    } finally {
      setActiveTransferRequestsLoading(false);
    }
  }, []);

  const handleActiveTransferDecision = async (
    request: ActiveTeamMemberTransferRequest,
    decision: "APPROVE" | "REJECT",
  ) => {
    if (processingActiveTransferRequestId) return;
    const note = decision === "REJECT"
      ? window.prompt("Please enter a reason for rejecting this transfer request:")
      : undefined;
    if (decision === "REJECT" && note === null) return;
    const confirmed = window.confirm(
      decision === "APPROVE"
        ? `Transfer ${request.memberName} to ${request.requestedHrAdminName}'s team?`
        : `Reject the active team member transfer request for ${request.memberName}?`,
    );
    if (!confirmed) return;

    setProcessingActiveTransferRequestId(request.id);
    try {
      const result = await decideActiveTeamMemberTransferRequest(request.id, decision, note?.trim() || undefined);
      await Promise.all([loadActiveTransferRequests(), loadNotifications()]);
      window.alert(result?.message || (decision === "APPROVE"
        ? "The team member has been transferred successfully."
        : "The active team member transfer request was rejected."));
    } catch (err: any) {
      console.error("Failed to decide active team member transfer request:", err);
      window.alert(err?.response?.data?.message || err?.message || "Unable to process the active team member transfer request.");
      await loadActiveTransferRequests();
    } finally {
      setProcessingActiveTransferRequestId(null);
    }
  };

  const toggleNotifications = () => {

    const nextOpen = !isOpen;

    setIsOpen(nextOpen);

    if (nextOpen) {

      void loadNotifications();

      void loadTransferRequests();
      void loadActiveTransferRequests();

    }

  };

  const handleMarkAsRead = async (notification: NotificationItem) => {

    if (notification.readAt || markingId) return;

    setMarkingId(notification.id);

    try {

      await notificationsApi.markAsRead(notification.id);

      setNotifications((current) =>

        current.map((item) =>

          item.id === notification.id

            ? { ...item, readAt: new Date().toISOString() }

            : item,

        ),

      );

      setUnreadCount((current) => Math.max(0, current - 1));

    } catch (err) {

      console.error("Failed to mark notification as read:", err);

      setError("Unable to update this notification.");

    } finally {

      setMarkingId(null);

    }

  };

  const handleMarkAllAsRead = async () => {

    if (isMarkingAll || unreadCount === 0) return;

    setIsMarkingAll(true);

    setError(null);

    try {

      await notificationsApi.markAllAsRead();

      const readAt = new Date().toISOString();

      setNotifications((current) =>

        current.map((item) => ({

          ...item,

          readAt: item.readAt ?? readAt,

        })),

      );

      setUnreadCount(0);

    } catch (err) {

      console.error("Failed to mark all notifications as read:", err);

      setError("Unable to mark notifications as read.");

    } finally {

      setIsMarkingAll(false);

    }

  };

  const openApprovalPopup = async (notification: NotificationItem) => {

    if (!notification.entityId) {

      setError("This notification does not contain an approval request ID.");

      return;

    }

    setIsOpen(false);

    setSelectedRequest(null);

    setIsLoadingRequest(true);

    setReviewError(null);

    setRejectReason("");

    setShowRejectForm(false);

    if (!notification.readAt) {

      void handleMarkAsRead(notification);

    }

    try {

      const isResultNotification =

        isApprovalResultNotification(notification);

      const response =

        isResultNotification && !canReviewApprovals

          ? await getMyJobApprovalRequestById(notification.entityId)

          : await getJobApprovalRequestById(notification.entityId);

      const request = response as

        | { request?: ApprovalRequest }

        | ApprovalRequest;

      const normalized =

        "request" in request && request.request

          ? request.request

          : (request as ApprovalRequest);

      if (!normalized?.id || !normalized.submittedData) {

        console.error("Unexpected approval request response:", response);

        setReviewError(

          "Approval details could not be read from the server response.",

        );

        return;

      }

      setSelectedRequest(normalized);

    } catch (err) {

      console.error("Failed to load approval request:", err);

      setReviewError(

        "Unable to load approval details. Check your access and try again.",

      );

    } finally {

      setIsLoadingRequest(false);

    }

  };

  const handleApprove = async () => {

    if (!selectedRequest || isReviewing) return;

    setIsReviewing(true);

    setReviewError(null);

    try {

      await approveJobRequest(selectedRequest.id);

      setSelectedRequest(null);

      setShowRejectForm(false);

      await loadNotifications();

    } catch (err) {

      console.error("Failed to approve job request:", err);

      setReviewError(

        "Approval failed. The request may have already been reviewed. Please refresh and try again.",

      );

    } finally {

      setIsReviewing(false);

    }

  };

  const handleReject = async () => {

    if (!selectedRequest || isReviewing) return;

    const reason = rejectReason.trim();

    if (!reason) {

      setReviewError("Please enter a reason for rejecting this request.");

      return;

    }

    setIsReviewing(true);

    setReviewError(null);

    try {

      await rejectJobRequest(selectedRequest.id, reason);

      setSelectedRequest(null);

      setShowRejectForm(false);

      setRejectReason("");

      await loadNotifications();

    } catch (err) {

      console.error("Failed to reject job request:", err);

      setReviewError(

        "Rejection failed. Please check the request and try again.",

      );

    } finally {

      setIsReviewing(false);

    }

  };

  const getNotificationLink = (notification: NotificationItem) => {

    if (!notification.entityId || !notification.entityType) return null;

    switch (notification.entityType.toUpperCase()) {

      case "JOB":

        return `/hr/jobs/${notification.entityId}/applicants`;

      case "APPLICATION":

        return `/hr/applications/${notification.entityId}`;

      default:

        return null;

    }

  };

  type UnifiedNotification =
    | {
      kind: "notification";
      id: string;
      createdAt: string;
      data: NotificationItem;
    }
    | {
      kind: "active-transfer-incoming";
      id: string;
      createdAt: string;
      data: ActiveTeamMemberTransferRequest;
    }
    | {
      kind: "active-transfer-mine";
      id: string;
      createdAt: string;
      data: ActiveTeamMemberTransferRequest;
    }
    | {
      kind: "reactivation-incoming";
      id: string;
      createdAt: string;
      data: ReactivationTransferRequest;
    }
    | {
      kind: "reactivation-mine";
      id: string;
      createdAt: string;
      data: ReactivationTransferRequest;
    };

  const unifiedNotifications: UnifiedNotification[] = [
    ...notifications.map((item) => ({
      kind: "notification" as const,
      id: `notification-${item.id}`,
      createdAt: item.createdAt,
      data: item,
    })),

    ...incomingActiveTransferRequests.map((item) => ({
      kind: "active-transfer-incoming" as const,
      id: `active-incoming-${item.id}`,
      createdAt: item.createdAt,
      data: item,
    })),

    ...myActiveTransferRequests.map((item) => ({
      kind: "active-transfer-mine" as const,
      id: `active-mine-${item.id}`,
      createdAt: item.createdAt,
      data: item,
    })),

    ...incomingTransferRequests.map((item) => ({
      kind: "reactivation-incoming" as const,
      id: `reactivation-incoming-${item.id}`,
      createdAt: item.createdAt,
      data: item,
    })),

    ...myTransferRequests.map((item) => ({
      kind: "reactivation-mine" as const,
      id: `reactivation-mine-${item.id}`,
      createdAt: item.createdAt,
      data: item,
    })),
  ].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() -
      new Date(a.createdAt).getTime(),
  );

  const isApprovalNotification = (notification: NotificationItem) =>

    notification.entityType?.toUpperCase() === "JOB_APPROVAL_REQUEST";

  return (

    <>

      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">

        <div className="flex min-h-16 items-center justify-between gap-3 px-4 py-2.5 sm:gap-4 sm:px-5 lg:px-6">

          <div className="flex min-w-0 items-center gap-3">

            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 shadow-sm">

              {PageIcon ? (

                <PageIcon className="h-5 w-5 text-white" />

              ) : (

                <span className="text-sm font-bold text-white">HR</span>

              )}

            </div>

            <div className="min-w-0">

              <h1 className="truncate text-base font-bold leading-tight text-slate-900 sm:text-lg">

                {pageTitle}

              </h1>

            </div>

          </div>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">

            <Button

              variant="outline"

              size="sm"

              asChild

              className="hidden h-9 rounded-lg border-slate-200 px-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 sm:inline-flex"

            >

              <Link to="/">

                <Building2 className="mr-1.5 h-4 w-4" />

                <span className="hidden lg:inline">Career Page</span>

                <span className="lg:hidden">Career</span>

              </Link>

            </Button>

            <div className="relative" ref={dropdownRef}>

              <Button

                type="button"

                variant="outline"

                size="icon"

                onClick={toggleNotifications}

                aria-label={`Notifications, ${unreadCount} unread`}

                aria-expanded={isOpen}

                aria-haspopup="dialog"

                className="relative h-10 w-10 rounded-xl border-slate-200 bg-white text-slate-700 hover:bg-slate-50"

              >

                <Bell className="h-5 w-5" />

                {unreadCount > 0 && (

                  <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-red-600 px-1 text-[10px] font-bold leading-none text-white">

                    {unreadCount > 99 ? "99+" : unreadCount}

                  </span>

                )}

              </Button>

              {isOpen && (

                <div

                  role="dialog"

                  aria-label="Notifications"

                  className="absolute right-0 top-12 z-50 w-[min(92vw,400px)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"

                >

                  <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">

                    <div>

                      <h2 className="text-sm font-bold text-slate-900">

                        Notifications

                      </h2>

                      <p className="mt-0.5 text-xs text-slate-500">

                        {unreadCount} unread

                      </p>

                    </div>

                    <div className="flex items-center gap-1">

                      <Button

                        type="button"

                        variant="ghost"

                        size="icon"

                        onClick={() => void loadNotifications()}

                        disabled={isLoading}

                        aria-label="Refresh notifications"

                        className="h-8 w-8 rounded-lg"

                      >

                        <RefreshCw

                          className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}

                        />

                      </Button>

                      <Button

                        type="button"

                        variant="ghost"

                        size="sm"

                        onClick={() => void handleMarkAllAsRead()}

                        disabled={isMarkingAll || unreadCount === 0}

                        className="h-8 gap-1 px-2 text-xs text-blue-700 hover:bg-blue-50"

                      >

                        {isMarkingAll ? (

                          <LoaderCircle className="h-3.5 w-3.5 animate-spin" />

                        ) : (

                          <CheckCheck className="h-3.5 w-3.5" />

                        )}

                        Mark all read

                      </Button>

                    </div>

                  </div>

                  {error && (

                    <div className="mx-3 mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">

                      {error}

                      <button

                        type="button"

                        onClick={() => void loadNotifications()}

                        className="ml-2 font-semibold underline"

                      >

                        Retry

                      </button>

                    </div>

                  )}
                  <div className="max-h-[240px] overflow-y-auto overscroll-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {isLoading && unifiedNotifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-slate-500">
                        Loading notifications...
                      </div>
                    ) : unifiedNotifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-slate-500">
                        No notifications yet.
                      </div>
                    ) : (
                      unifiedNotifications.map((item) => {
                        // 1. Normal notifications
                        if (item.kind === "notification") {
                          const notification = item.data;
                          const destination = getNotificationLink(notification);
                          const isUnread = !notification.readAt;
                          const isMarking = markingId === notification.id;
                          const isApproval =
                            notification.entityType?.toUpperCase() ===
                            "JOB_APPROVAL_REQUEST";
                          const isApprovalResult = isApprovalResultNotification(notification);
                          const isAssignedJob =
                            notification.eventType?.toUpperCase() === "JOB_ASSIGNED";

                          const content = (
                            <div className="flex items-start gap-3">
                              {isUnread && (
                                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600" />
                              )}

                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-semibold text-slate-900">
                                    {notification.title}
                                  </p>

                                  {isUnread && (
                                    <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                                      New
                                    </span>
                                  )}
                                </div>

                                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">
                                  {notification.message}
                                </p>

                                {(isApproval || destination) && (
                                  <p className="mt-2 text-xs font-medium text-blue-600">
                                    {isApproval
                                      ? isApprovalResult
                                        ? "Click to view request details →"
                                        : "Click to review request →"
                                      : isAssignedJob
                                        ? "View assigned job →"
                                        : "View details →"}
                                  </p>
                                )}

                                <p className="mt-2 text-xs text-slate-400">
                                  {formatNotificationTime(item.createdAt)}
                                </p>
                              </div>
                            </div>
                          );

                          return (
                            <div
                              key={item.id}
                              className={`border-b border-slate-100 px-4 py-3 transition-colors hover:bg-slate-50 ${isUnread ? "bg-blue-50/40" : "bg-white"
                                }`}
                            >
                              <div className="flex items-start gap-2">
                                <div className="min-w-0 flex-1">
                                  {isApproval ? (
                                    <button
                                      type="button"
                                      className="w-full text-left"
                                      onClick={() => openApprovalPopup(notification)}
                                    >
                                      {content}
                                    </button>
                                  ) : destination ? (
                                    <Link
                                      to={destination}
                                      className="block"
                                      onClick={() => {
                                        setIsOpen(false);
                                        if (isUnread) {
                                          void handleMarkAsRead(notification);
                                        }
                                      }}
                                    >
                                      {content}
                                    </Link>
                                  ) : (
                                    content
                                  )}

                                  {isAssignedJob && destination && (
                                    <Link
                                      to={destination}
                                      className="mt-2 inline-flex rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                                      onClick={() => {
                                        setIsOpen(false);
                                        if (isUnread) {
                                          void handleMarkAsRead(notification);
                                        }
                                      }}
                                    >
                                      View Assigned Job
                                    </Link>
                                  )}
                                </div>

                                {isUnread && (
                                  <button
                                    type="button"
                                    disabled={isMarking}
                                    className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void handleMarkAsRead(notification);
                                    }}
                                  >
                                    {isMarking ? "..." : "Mark read"}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        }

                        // 2. Incoming active team-member transfer requests
                        if (item.kind === "active-transfer-incoming") {
                          const request = item.data;
                          const status = request.status?.toUpperCase();
                          const statusClass =
                            status === "APPROVED"
                              ? "bg-green-100 text-green-700"
                              : status === "REJECTED"
                                ? "bg-red-100 text-red-700"
                                : "bg-amber-100 text-amber-700";

                          return (
                            <div
                              key={item.id}
                              className="border-b border-slate-100 px-4 py-3 hover:bg-slate-50"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900">
                                    Active team-member transfer request
                                  </p>

                                  <p className="mt-1 text-sm text-slate-600">
                                    <span className="font-medium">{request.requestedByName}</span>
                                    {" requested to transfer "}
                                    <span className="font-medium">
                                      {request.memberName}
                                    </span>
                                    {" ("}
                                    {request.memberEmail || "this team member"}
                                    {") to "}
                                    <span className="font-medium">
                                      {request.requestedHrAdminName}
                                    </span>
                                    {"'s team."}
                                  </p>

                                  <p className="mt-2 text-xs text-slate-400">
                                    {formatNotificationTime(item.createdAt)}
                                  </p>
                                </div>

                                <span
                                  className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${statusClass}`}
                                >
                                  {status || "PENDING"}
                                </span>
                              </div>

                              {status === "PENDING" && (
                                <div className="mt-3 flex gap-2">
                                  <button
                                    type="button"
                                    className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                    onClick={() =>
                                      void handleActiveTransferDecision(request, "APPROVE")
                                    }
                                  >
                                    Approve
                                  </button>

                                  <button
                                    type="button"
                                    className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                                    onClick={() =>
                                      void handleActiveTransferDecision(request, "REJECT")
                                    }
                                  >
                                    Reject
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        }


                        // 3. Active transfer requests submitted by the current HR Admin
                        if (item.kind === "active-transfer-mine") {
                          const request = item.data;
                          const status = request.status?.toUpperCase();

                          const message =
                            status === "APPROVED"
                              ? `${request.memberName} has been transferred to your team.`
                              : status === "REJECTED"
                                ? `The transfer request was rejected by ${request.currentHrAdminName || "the current HR Admin"
                                }. Ownership has not changed.`
                                : `Your request to transfer ${request.memberName} to ${request.requestedHrAdminName}'s team is awaiting approval from ${request.currentHrAdminName || "the current HR Admin"
                                }.`;

                          const statusClass =
                            status === "APPROVED"
                              ? "bg-green-100 text-green-700"
                              : status === "REJECTED"
                                ? "bg-red-100 text-red-700"
                                : "bg-amber-100 text-amber-700";

                          return (
                            <div
                              key={item.id}
                              className="border-b border-slate-100 px-4 py-3 hover:bg-slate-50"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900">
                                    Active transfer request
                                  </p>

                                  <p className="mt-1 text-sm text-slate-600">{message}</p>

                                  {request.reviewerNote && (
                                    <p className="mt-2 rounded-md bg-slate-50 p-2 text-xs text-slate-600">
                                      <span className="font-semibold">Reviewer note:</span>{" "}
                                      {request.reviewerNote}
                                    </p>
                                  )}

                                  <p className="mt-2 text-xs text-slate-400">
                                    {formatNotificationTime(item.createdAt)}
                                  </p>
                                </div>

                                <span
                                  className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${statusClass}`}
                                >
                                  {status || "PENDING"}
                                </span>
                              </div>
                            </div>
                          );
                        }

                        // 4. Incoming reactivation transfer requests
                        if (item.kind === "reactivation-incoming") {
                          const request = item.data;
                          const status = request.status?.toUpperCase();

                          return (
                            <div
                              key={item.id}
                              className="border-b border-slate-100 px-4 py-3 hover:bg-slate-50"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900">
                                    Transfer and reactivation request: {request.memberName}
                                  </p>

                                  <p className="mt-1 text-sm text-slate-600">
                                    {request.requestedByName} requested to transfer and reactivate{" "}
                                    {request.memberEmail || request.memberName} under{" "}
                                    {request.requestedHrAdminName}.
                                  </p>

                                  <p className="mt-2 text-xs text-slate-400">
                                    {formatNotificationTime(item.createdAt)}
                                  </p>
                                </div>

                                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-700">
                                  {status || "PENDING"}
                                </span>
                              </div>

                              {status === "PENDING" && (
                                <div className="mt-3 flex gap-2">
                                  <button
                                    type="button"
                                    className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                    onClick={() => void handleTransferDecision(request, "APPROVE")}
                                  >
                                    Approve
                                  </button>

                                  <button
                                    type="button"
                                    className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                                    onClick={() => void handleTransferDecision(request, "REJECT")}
                                  >
                                    Reject
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        }

                        // 5. Reactivation transfer requests submitted by the current HR Admin
                        if (item.kind === "reactivation-mine") {
                          const request = item.data;
                          const status = request.status?.toUpperCase();
                          const previousAdmin =
                            request.currentHrAdminName || "the previous HR Admin";

                          const message =
                            status === "APPROVED"
                              ? `${request.memberName} has been transferred to your team and reactivated.`
                              : status === "REJECTED"
                                ? `Rejected by ${previousAdmin}; the member remains deleted and ownership has not changed.`
                                : `Your request was sent to ${previousAdmin}. Once approved, the member will transfer to your team and reactivate.`;

                          const statusClass =
                            status === "APPROVED"
                              ? "bg-green-100 text-green-700"
                              : status === "REJECTED"
                                ? "bg-red-100 text-red-700"
                                : "bg-amber-100 text-amber-700";

                          return (
                            <div
                              key={item.id}
                              className="border-b border-slate-100 px-4 py-3 hover:bg-slate-50"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900">
                                    Transfer and reactivation request
                                  </p>

                                  <p className="mt-1 text-sm text-slate-600">{message}</p>

                                  {request.reviewerNote && (
                                    <p className="mt-2 rounded-md bg-slate-50 p-2 text-xs text-slate-600">
                                      <span className="font-semibold">Reviewer note:</span>{" "}
                                      {request.reviewerNote}
                                    </p>
                                  )}

                                  <p className="mt-2 text-xs text-slate-400">
                                    {formatNotificationTime(item.createdAt)}
                                  </p>
                                </div>

                                <span
                                  className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${statusClass}`}
                                >
                                  {status || "PENDING"}
                                </span>
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })
                    )}

                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-2">

                      <p className="text-center text-[11px] text-slate-500">

                        Showing the latest {unifiedNotifications.length} notifications

                      </p>
                    </div>

                  </div>

                </div>

              )}

            </div>

            <div className="flex items-center gap-2 rounded-xl px-1.5 py-1.5 sm:px-2">

              <div

                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 text-sm font-bold text-white shadow-sm sm:h-10 sm:w-10"

                title={displayName}

              >

                {avatarLetter}

              </div>

              <div className="hidden min-w-0 max-w-[145px] flex-col leading-tight lg:flex">

                <span className="truncate text-sm font-semibold text-slate-900">

                  {displayName}

                </span>

                <span className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">

                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />

                  <span className="capitalize">{displayRole}</span>

                </span>

              </div>

            </div>

          </div>

        </div>

      </header>

      {/* Job approval details popup */}

      {(isLoadingRequest || selectedRequest || reviewError) && (

        <div

          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6"

          onMouseDown={(event) => {

            if (event.target === event.currentTarget && !isReviewing) {

              setSelectedRequest(null);

              setReviewError(null);

              setShowRejectForm(false);

            }

          }}

        >

          <section

            role="dialog"

            aria-modal="true"

            aria-labelledby="approval-popup-title"

            className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"

          >

            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 sm:px-6">

              <div className="flex items-center gap-3">

                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">

                  <FileText className="h-5 w-5" />

                </div>

                <div>

                  <h2

                    id="approval-popup-title"

                    className="text-lg font-bold text-slate-900"

                  >

                    Job Approval Request

                  </h2>

                  <p className="text-xs text-slate-500">

                    Review the submitted job details

                  </p>

                </div>

              </div>

              <Button

                type="button"

                variant="ghost"

                size="icon"

                aria-label="Close popup"

                disabled={isReviewing}

                onClick={() => {

                  setSelectedRequest(null);

                  setReviewError(null);

                  setShowRejectForm(false);

                }}

              >

                <X className="h-5 w-5" />

              </Button>

            </div>

            {isLoadingRequest ? (

              <div className="flex min-h-64 items-center justify-center gap-3 text-sm text-slate-600">

                <LoaderCircle className="h-5 w-5 animate-spin text-blue-600" />

                Loading job approval details...

              </div>

            ) : (

              <>

                {selectedRequest && (

                  <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">

                    {/* Request metadata */}

                    <div className="grid gap-3 sm:grid-cols-2">

                      <div className="rounded-xl border border-slate-200 p-4">

                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">

                          Request type

                        </p>

                        <p className="mt-1 font-semibold text-slate-900">

                          {formatLabel(selectedRequest.requestType || "CREATE")}

                        </p>

                      </div>

                      <div className="rounded-xl border border-slate-200 p-4">

                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">

                          Requested job status

                        </p>

                        <p className="mt-1 font-semibold text-slate-900">

                          {formatLabel(

                            selectedRequest.requestedStatus || "PUBLISHED",

                          )}

                        </p>

                      </div>

                    </div>

                    {/* Submitted by */}

                    <div className="rounded-xl border border-slate-200 p-4">

                      <h3 className="font-semibold text-slate-900">

                        Submitted by

                      </h3>

                      <p className="mt-2 text-sm text-slate-700">

                        {selectedRequest.requester?.name || "Team Member"}

                      </p>

                      {selectedRequest.requester?.email && (

                        <p className="mt-1 text-sm text-slate-500">

                          {selectedRequest.requester.email}

                        </p>

                      )}

                    </div>

                    {/* Approval status */}

                    <div className="rounded-xl border border-slate-200 p-4">

                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">

                        Approval status

                      </p>

                      <p className="mt-1 font-semibold text-slate-900">

                        {formatLabel(selectedRequest.status || "PENDING")}

                      </p>

                    </div>

                    {/* Job Details - JobDetailPage style */}

                    {selectedRequest.submittedData &&

                      Object.keys(selectedRequest.submittedData).length > 0 ? (

                      (() => {

                        const data = selectedRequest.submittedData!;

                        const title = getSubmittedValue(

                          data,

                          "title",

                          "jobTitle",

                        );

                        const department = getSubmittedValue(

                          data,

                          "department",

                        );

                        const location = getSubmittedValue(data, "location");

                        const employmentType = getSubmittedValue(

                          data,

                          "employmentType",

                        );

                        const description = getSubmittedValue(

                          data,

                          "description",

                          "jobDescription",

                        );

                        const aboutRole = getSubmittedValue(

                          data,

                          "aboutRole",

                        );

                        const responsibilities = getDisplayList(

                          getSubmittedValue(

                            data,

                            "responsibilities",

                            "keyResponsibilities",

                          ),

                        );

                        const requirements = getDisplayList(

                          getSubmittedValue(

                            data,

                            "requirements",

                            "qualifications",

                          ),

                        );

                        const skills = getDisplayList(

                          getSubmittedValue(

                            data,

                            "skills",

                            "requiredSkills",

                          ),

                        );

                        const benefits = getDisplayList(

                          getSubmittedValue(data, "benefits"),

                        );

                        const excludedKeys = [

                          "title",

                          "jobTitle",

                          "department",

                          "location",

                          "employmentType",

                          "description",

                          "jobDescription",

                          "aboutRole",

                          "responsibilities",

                          "keyResponsibilities",

                          "requirements",

                          "qualifications",

                          "skills",

                          "requiredSkills",

                          "benefits",

                          "assignedUsers",

                          "assignedToUsers",

                          "assignedToNames",

                        ].map((key) =>

                          key.replace(/[\_\s-]/g, "").toLowerCase(),

                        );

                        const assignedIds = getDisplayList(

                          getSubmittedValue(

                            data,

                            "assignedToIds",

                            "assignedToId",

                          ),

                        );

                        const providedAssignedNames = getDisplayList(

                          getSubmittedValue(

                            data,

                            "assignedUsers",

                            "assignedToUsers",

                            "assignedToNames",

                          ),

                        );

                        const assignedNamesText =

                          (providedAssignedNames.length > 0

                            ? providedAssignedNames

                            : assignedIds.map(

                              (id) => assignedUserNames[id] ?? "Loading...",

                            )

                          ).join("\n") || "—";

                        const quickInfo = Object.entries(data).filter(

                          ([key, value]) =>

                            !excludedKeys.includes(

                              key.replace(/[\_\s-]/g, "").toLowerCase(),

                            ) &&

                            value !== null &&

                            value !== undefined &&

                            value !== "",

                        );

                        const renderList = (

                          items: string[],

                          type: "check" | "bullet",

                        ) =>

                          items.length > 0 ? (

                            <ul className="space-y-3">

                              {items.map((item, index) => (

                                <li

                                  key={`${item}-${index}`}

                                  className="flex items-start gap-3"

                                >

                                  {type === "check" ? (

                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />

                                  ) : (

                                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />

                                  )}

                                  <span className="break-words text-sm leading-relaxed text-slate-600">

                                    {item}

                                  </span>

                                </li>

                              ))}

                            </ul>

                          ) : (

                            <p className="text-sm text-slate-400">

                              Not specified

                            </p>

                          );

                        return (

                          <section className="space-y-5">

                            {/* Job Header */}

                            <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">

                              <div className="flex flex-wrap items-center gap-2">

                                {employmentType != null &&

                                  employmentType !== false &&

                                  employmentType !== "" && (

                                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">

                                      {formatLabel(String(employmentType))}

                                    </span>

                                  )}

                                <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">

                                  {formatLabel(

                                    selectedRequest.requestType || "CREATE",

                                  )}{" "}

                                  Request

                                </span>

                              </div>

                              <h3 className="mt-3 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">

                                {title ? formatValue(title) : "Job Details"}

                              </h3>

                              {department != null &&

                                department !== false &&

                                department !== "" && (

                                  <p className="mt-1 text-sm text-slate-500">

                                    {formatValue(department)}

                                  </p>

                                )}

                              <div className="mt-5 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2">

                                {[

                                  { label: "Location", value: location },

                                  {

                                    label: "Employment Type",

                                    value: employmentType,

                                  },

                                ].map((item) => (

                                  <div

                                    key={item.label}

                                    className="flex items-start gap-2"

                                  >

                                    <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />

                                    <div className="min-w-0">

                                      <p className="text-xs text-slate-500">

                                        {item.label}

                                      </p>

                                      <p className="break-words text-sm font-medium text-slate-800">

                                        {item.value

                                          ? formatValue(item.value)

                                          : "Not specified"}

                                      </p>

                                    </div>

                                  </div>

                                ))}

                              </div>

                            </div>

                            {/* Main Content + Sidebar */}

                            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)\_minmax(280px,1fr)]">

                              {/* Main Content */}

                              <div className="min-w-0 space-y-4">

                                {/* About the Role */}

                                {(description != null &&

                                  description !== false &&

                                  description !== "") ||

                                  (aboutRole != null &&

                                    aboutRole !== false &&

                                    aboutRole !== "") ? (

                                  <div className="rounded-xl border border-slate-200 bg-white p-5">

                                    <h4 className="mb-3 text-base font-semibold text-slate-900">

                                      About the Role

                                    </h4>

                                    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-600">

                                      {formatValue(aboutRole ?? description)}

                                    </p>

                                    {aboutRole != null &&

                                      aboutRole !== false &&

                                      aboutRole !== "" &&

                                      description != null &&

                                      description !== false &&

                                      description !== "" && (

                                        <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-slate-600">

                                          {formatValue(description)}

                                        </p>

                                      )}

                                  </div>

                                ) : null}

                                {/* Key Responsibilities */}

                                <div className="rounded-xl border border-slate-200 bg-white p-5">

                                  <h4 className="mb-4 text-base font-semibold text-slate-900">

                                    Key Responsibilities

                                  </h4>

                                  {renderList(responsibilities, "check")}

                                </div>

                                {/* Requirements */}

                                <div className="rounded-xl border border-slate-200 bg-white p-5">

                                  <h4 className="mb-4 text-base font-semibold text-slate-900">

                                    Requirements

                                  </h4>

                                  {renderList(requirements, "bullet")}

                                </div>

                                {/* Required Skills */}

                                <div className="rounded-xl border border-slate-200 bg-white p-5">

                                  <h4 className="mb-3 text-base font-semibold text-slate-900">

                                    Required Skills

                                  </h4>

                                  {skills.length > 0 ? (

                                    <div className="flex flex-wrap gap-2">

                                      {skills.map((skill, index) => (

                                        <span

                                          key={`${skill}-${index}`}

                                          className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700"

                                        >

                                          {skill}

                                        </span>

                                      ))}

                                    </div>

                                  ) : (

                                    <p className="text-sm text-slate-400">Not specified</p>

                                  )}

                                </div>

                              </div>

                              {/* Sidebar */}

                              <aside className="min-w-0 space-y-4">

                                {/* Benefits */}

                                {benefits.length > 0 && (

                                  <div className="rounded-xl border border-slate-200 bg-white p-5">

                                    <h4 className="mb-4 text-base font-semibold text-slate-900">

                                      Benefits

                                    </h4>

                                    {renderList(benefits, "check")}

                                  </div>

                                )}

                                {/* Quick Info - fixed height with its own scroll */}

                                <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">

                                  <div className="shrink-0 border-b border-slate-100 bg-slate-50 px-4 py-3">

                                    <h4 className="text-sm font-semibold text-slate-900">

                                      Quick Info

                                    </h4>

                                  </div>

                                  <div className="grid max-h-72 grid-cols-1 gap-x-4 overflow-y-auto overscroll-contain px-4 sm:max-h-80 sm:grid-cols-2 lg:max-h-[420px] lg:grid-cols-1">

                                    {quickInfo.map(([key, value]) => {

                                      const isAssignedKey =

                                        key

                                          .replace(/[\_\s-]/g, "")

                                          .toLowerCase() === "assignedtoids" ||

                                        key

                                          .replace(/[\_\s-]/g, "")

                                          .toLowerCase() === "assignedtoid";

                                      return (

                                        <div

                                          key={key}

                                          className="min-w-0 border-b border-slate-100 py-3 last:border-b-0"

                                        >

                                          <p className="mb-1 text-xs font-medium text-slate-500">

                                            {isAssignedKey

                                              ? "Assigned To"

                                              : formatLabel(key)}

                                          </p>

                                          <p className="whitespace-pre-wrap break-words text-sm leading-5 text-slate-800">

                                            {isAssignedKey

                                              ? assignedNamesText

                                              : formatValue(value)}

                                          </p>

                                        </div>

                                      );

                                    })}

                                  </div>

                                </div>

                              </aside>

                            </div>

                          </section>

                        );

                      })()

                    ) : (

                      <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">

                        No submitted job details were returned by the server.

                      </p>

                    )}

                    {/* Review comments */}

                    {selectedRequest.comments && (

                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">

                        <h3 className="text-sm font-semibold text-amber-900">

                          {selectedRequest.status === "REJECTED"

                            ? "Rejection reason"

                            : selectedRequest.status === "CHANGES_REQUESTED"

                              ? "Requested changes"

                              : "Review comments"}

                        </h3>

                        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-amber-800">

                          {selectedRequest.comments}

                        </p>

                      </div>

                    )}

                    {/* Rejection form */}

                    {showRejectForm && (

                      <div className="rounded-xl border border-red-200 bg-red-50/60 p-4">

                        <label

                          htmlFor="job-rejection-reason"

                          className="block text-sm font-semibold text-red-900"

                        >

                          Reason for rejection \\\*

                        </label>

                        <textarea

                          id="job-rejection-reason"

                          value={rejectReason}

                          onChange={(event) =>

                            setRejectReason(event.target.value)

                          }

                          placeholder="Explain why this job request is being rejected..."

                          rows={3}

                          maxLength={2000}

                          className="mt-2 w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"

                        />

                      </div>

                    )}

                    {reviewError && (

                      <div

                        role="alert"

                        className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"

                      >

                        {reviewError}

                      </div>

                    )}

                  </div>

                )}

                {!selectedRequest && reviewError && (

                  <div className="p-6">

                    <div

                      role="alert"

                      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"

                    >

                      {reviewError}

                    </div>

                    <Button

                      type="button"

                      variant="outline"

                      className="mt-4"

                      onClick={() => setReviewError(null)}

                    >

                      Close

                    </Button>

                  </div>

                )}

                {selectedRequest && (

                  <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">

                    <Button

                      type="button"

                      variant="outline"

                      disabled={isReviewing}

                      onClick={() => {

                        setSelectedRequest(null);

                        setReviewError(null);

                        setShowRejectForm(false);

                      }}

                    >

                      Close

                    </Button>

                    {canReviewApprovals &&

                      selectedRequest.status === "PENDING" && (

                        <div className="flex flex-col gap-2 sm:flex-row">

                          {showRejectForm ? (

                            <>

                              <Button

                                type="button"

                                variant="outline"

                                disabled={isReviewing}

                                onClick={() => {

                                  setShowRejectForm(false);

                                  setRejectReason("");

                                  setReviewError(null);

                                }}

                              >

                                Cancel

                              </Button>

                              <Button

                                type="button"

                                disabled={

                                  isReviewing || !rejectReason.trim()

                                }

                                onClick={() => void handleReject()}

                                className="bg-red-600 text-white hover:bg-red-700"

                              >

                                {isReviewing ? (

                                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />

                                ) : (

                                  <XCircle className="mr-2 h-4 w-4" />

                                )}

                                Confirm Rejection

                              </Button>

                            </>

                          ) : (

                            <>

                              <Button

                                type="button"

                                variant="outline"

                                disabled={isReviewing}

                                onClick={() => {

                                  setShowRejectForm(true);

                                  setReviewError(null);

                                }}

                                className="border-red-200 text-red-700 hover:bg-red-50"

                              >

                                <XCircle className="mr-2 h-4 w-4" />

                                Reject

                              </Button>

                              <Button

                                type="button"

                                disabled={isReviewing}

                                onClick={() => void handleApprove()}

                                className="bg-emerald-600 text-white hover:bg-emerald-700"

                              >

                                {isReviewing ? (

                                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />

                                ) : (

                                  <CheckCircle2 className="mr-2 h-4 w-4" />

                                )}

                                Approve Job

                              </Button>

                            </>

                          )}

                        </div>

                      )}

                  </div>

                )}

              </>

            )}

          </section>

        </div>

      )}

    </>

  );

}